import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { serviceDeliveryAPI, brandKitAPI } from '../lib/api';
import {
  Library, Save, Lock, Plus, Trash2, Loader2, Quote, Lightbulb, Trophy,
  FileText, Repeat, Check, Upload, ExternalLink, Image as ImageIcon,
} from 'lucide-react';

const uid = () => Math.random().toString(36).slice(2, 10);

export default function ContentLibrarySection({ clientId }) {
  const [lib, setLib] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [uploadingId, setUploadingId] = useState('');
  const fileInputs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await serviceDeliveryAPI.getLibrary(clientId, 'content-library');
      setLib(res?.data || {});
      setCanEdit(!!res?.can_edit);
      setDirty(false);
    } catch { toast.error('Error cargando la biblioteca de contenido'); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const setList = (key, next) => { setLib((p) => ({ ...p, [key]: next })); setDirty(true); };
  const get = (key) => Array.isArray(lib?.[key]) ? lib[key] : [];

  const handleSave = async () => {
    setSaving(true);
    try { await serviceDeliveryAPI.saveLibrary(clientId, 'content-library', lib); toast.success('Biblioteca guardada'); await load(); }
    catch (e) { toast.error(e?.message || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const uploadCaptura = async (itemId, file) => {
    if (!file) return;
    setUploadingId(itemId);
    try {
      const rec = await brandKitAPI.uploadFile(clientId, file);
      const ref = { id: rec.id, name: rec.name, content_type: rec.content_type };
      setList('ganadores', get('ganadores').map((g) => g.id === itemId ? { ...g, captura: ref } : g));
    } catch (e) { toast.error(e?.message || 'Error subiendo captura'); }
    finally { setUploadingId(''); }
  };

  if (loading || !lib) return <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  const RO = !canEdit;

  const Section = ({ icon: Icon, title, desc, k, onAdd, children }) => (
    <Card data-testid={`content-lib-${k}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Icon className="w-4 h-4 text-primary" /> {title}</CardTitle>
            {desc && <p className="text-xs text-muted-foreground mt-1">{desc}</p>}
          </div>
          {!RO && <Button size="sm" variant="outline" onClick={onAdd} data-testid={`content-add-${k}`}><Plus className="w-4 h-4 mr-1" /> Añadir</Button>}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );

  const empty = (t) => <p className="text-sm text-muted-foreground italic">{t}</p>;

  return (
    <div className="space-y-5" data-testid="content-library-section">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2"><Library className="w-5 h-5 text-primary" /> Contenido (biblioteca)</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Banco de ideas, ganchos y lo que ya funcionó, para planificar el mes sin partir de cero.</p>
            </div>
            {RO ? <Badge variant="secondary" className="gap-1"><Lock className="w-3 h-3" /> Solo lectura</Badge> : (
              <Button onClick={handleSave} disabled={saving || !dirty} data-testid="content-library-save">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{dirty ? 'Guardar' : 'Guardado'}
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* GANCHOS */}
      <Section icon={Quote} title="Ganchos que funcionan" desc="Frases de apertura de reels/posts que han funcionado, para reutilizar." k="ganchos"
        onAdd={() => setList('ganchos', [...get('ganchos'), { id: uid(), texto: '', nota: '' }])}>
        {get('ganchos').length === 0 ? empty('Sin ganchos.') : (
          <div className="space-y-2">
            {get('ganchos').map((g) => (
              <div key={g.id} className="rounded-lg border p-3 space-y-2" data-testid={`content-gancho-${g.id}`}>
                <div className="flex items-start gap-2">
                  <Textarea value={g.texto} onChange={(e) => setList('ganchos', get('ganchos').map((x) => x.id === g.id ? { ...x, texto: e.target.value } : x))} disabled={RO} rows={2} placeholder="Gancho / frase de apertura" className="resize-none" data-testid={`content-gancho-texto-${g.id}`} />
                  {!RO && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0" onClick={() => setList('ganchos', get('ganchos').filter((x) => x.id !== g.id))}><Trash2 className="w-4 h-4" /></Button>}
                </div>
                <Input value={g.nota} onChange={(e) => setList('ganchos', get('ganchos').map((x) => x.id === g.id ? { ...x, nota: e.target.value } : x))} disabled={RO} placeholder="Nota opcional: en qué funcionó" className="h-8 text-xs" />
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* BANCO DE IDEAS */}
      <Section icon={Lightbulb} title="Banco de ideas" desc="Ideas sueltas para cuando toque planificar. Márcalas como usada al convertirlas en publicación." k="ideas"
        onAdd={() => setList('ideas', [...get('ideas'), { id: uid(), texto: '', usada: false }])}>
        {get('ideas').length === 0 ? empty('Sin ideas.') : (
          <div className="space-y-2">
            {get('ideas').map((it) => (
              <div key={it.id} className={`flex items-center gap-2 rounded-lg border p-2 ${it.usada ? 'bg-muted/40' : ''}`} data-testid={`content-idea-${it.id}`}>
                <button type="button" disabled={RO} onClick={() => setList('ideas', get('ideas').map((x) => x.id === it.id ? { ...x, usada: !x.usada } : x))} className={`h-6 w-6 rounded-full border flex items-center justify-center shrink-0 ${it.usada ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white'}`} data-testid={`content-idea-toggle-${it.id}`} title={it.usada ? 'Usada' : 'Marcar como usada'}>
                  {it.usada && <Check className="w-3.5 h-3.5" />}
                </button>
                <Input value={it.texto} onChange={(e) => setList('ideas', get('ideas').map((x) => x.id === it.id ? { ...x, texto: e.target.value } : x))} disabled={RO} placeholder="Idea rápida de contenido" className={`h-8 ${it.usada ? 'line-through text-muted-foreground' : ''}`} data-testid={`content-idea-texto-${it.id}`} />
                {it.usada && <Badge variant="secondary" className="text-[10px] shrink-0">Usada</Badge>}
                {!RO && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0" onClick={() => setList('ideas', get('ideas').filter((x) => x.id !== it.id))}><Trash2 className="w-3.5 h-3.5" /></Button>}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* CONTENIDO GANADOR */}
      <Section icon={Trophy} title="Contenido ganador" desc="Los posts/reels que mejor han funcionado, como referencia para repetir fórmula." k="ganadores"
        onAdd={() => setList('ganadores', [...get('ganadores'), { id: uid(), enlace: '', captura: null, nota: '' }])}>
        {get('ganadores').length === 0 ? empty('Sin contenido ganador.') : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {get('ganadores').map((g) => (
              <div key={g.id} className="rounded-lg border p-3 space-y-2" data-testid={`content-ganador-${g.id}`}>
                <div className="flex items-center gap-2">
                  <Input value={g.enlace} onChange={(e) => setList('ganadores', get('ganadores').map((x) => x.id === g.id ? { ...x, enlace: e.target.value } : x))} disabled={RO} placeholder="Enlace al post/reel" className="h-8" data-testid={`content-ganador-enlace-${g.id}`} />
                  {!RO && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0" onClick={() => setList('ganadores', get('ganadores').filter((x) => x.id !== g.id))}><Trash2 className="w-4 h-4" /></Button>}
                </div>
                {g.captura && <img src={brandKitAPI.fileUrl(g.captura.id)} alt="captura" className="rounded-md border object-cover w-full h-32" />}
                <div className="flex items-center gap-2 flex-wrap">
                  {g.enlace && <a href={g.enlace} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Abrir</Button></a>}
                  {!RO && (
                    <>
                      <input ref={(el) => (fileInputs.current[g.id] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadCaptura(g.id, e.target.files?.[0]); e.target.value = ''; }} data-testid={`content-ganador-file-${g.id}`} />
                      <Button type="button" variant="outline" size="sm" onClick={() => fileInputs.current[g.id]?.click()} disabled={uploadingId === g.id}>
                        {uploadingId === g.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} {g.captura ? 'Cambiar captura' : 'Captura'}
                      </Button>
                    </>
                  )}
                </div>
                <Textarea value={g.nota} onChange={(e) => setList('ganadores', get('ganadores').map((x) => x.id === g.id ? { ...x, nota: e.target.value } : x))} disabled={RO} rows={2} placeholder="Por qué funcionó" className="resize-none text-sm" />
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* GUIONES / PLANTILLAS */}
      <Section icon={FileText} title="Guiones / plantillas reutilizables" desc="Estructuras de guion o plantillas que sirven una y otra vez." k="guiones"
        onAdd={() => setList('guiones', [...get('guiones'), { id: uid(), titulo: '', contenido: '' }])}>
        {get('guiones').length === 0 ? empty('Sin guiones.') : (
          <div className="space-y-2">
            {get('guiones').map((g) => (
              <div key={g.id} className="rounded-lg border p-3 space-y-2" data-testid={`content-guion-${g.id}`}>
                <div className="flex items-center gap-2">
                  <Input value={g.titulo} onChange={(e) => setList('guiones', get('guiones').map((x) => x.id === g.id ? { ...x, titulo: e.target.value } : x))} disabled={RO} placeholder="Título del guion/plantilla" className="h-8 font-medium" data-testid={`content-guion-titulo-${g.id}`} />
                  {!RO && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0" onClick={() => setList('guiones', get('guiones').filter((x) => x.id !== g.id))}><Trash2 className="w-4 h-4" /></Button>}
                </div>
                <Textarea value={g.contenido} onChange={(e) => setList('guiones', get('guiones').map((x) => x.id === g.id ? { ...x, contenido: e.target.value } : x))} disabled={RO} rows={3} placeholder="Estructura del guion / plantilla" className="resize-none" />
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* SERIES / FORMATOS */}
      <Section icon={Repeat} title="Series / formatos recurrentes" desc="Secciones fijas del cliente (ej. 'el consejo del lunes')." k="series"
        onAdd={() => setList('series', [...get('series'), { id: uid(), nombre: '', descripcion: '' }])}>
        {get('series').length === 0 ? empty('Sin series.') : (
          <div className="space-y-2">
            {get('series').map((s) => (
              <div key={s.id} className="rounded-lg border p-3 space-y-2" data-testid={`content-serie-${s.id}`}>
                <div className="flex items-center gap-2">
                  <Input value={s.nombre} onChange={(e) => setList('series', get('series').map((x) => x.id === s.id ? { ...x, nombre: e.target.value } : x))} disabled={RO} placeholder="Nombre de la serie/formato" className="h-8 font-medium" data-testid={`content-serie-nombre-${s.id}`} />
                  {!RO && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0" onClick={() => setList('series', get('series').filter((x) => x.id !== s.id))}><Trash2 className="w-4 h-4" /></Button>}
                </div>
                <Textarea value={s.descripcion} onChange={(e) => setList('series', get('series').map((x) => x.id === s.id ? { ...x, descripcion: e.target.value } : x))} disabled={RO} rows={2} placeholder="Descripción" className="resize-none" />
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
